import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BarChart, TrendingUp, MousePointerClick, LogOut, Package, Store, Sparkles } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

function AdminDashboard() {
  const [analytics, setAnalytics] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem('admin_token');
      if (!token) {
        navigate('/admin');
        return;
      }

      try {
        const response = await axios.get(`${API}/admin/analytics/summary`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAnalytics(response.data);
      } catch (error) {
        if (error.response && error.response.status === 401) {
          localStorage.removeItem('admin_token');
          navigate('/admin');
        }
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [navigate]);

  const handleLogout = async () => {
    const token = localStorage.getItem('admin_token');
    try {
      await axios.post(`${API}/admin/logout`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    localStorage.removeItem('admin_token');
    navigate('/admin');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-[#D4AF37] text-xl">Loading...</div>
      </div>
    );
  }

  const totalClicks = analytics && analytics.total_clicks ? analytics.total_clicks : 0;
  const recentClicks = analytics && analytics.recent_clicks ? analytics.recent_clicks : 0;
  const topRetailers = analytics && analytics.top_retailers ? analytics.top_retailers : [];
  const topDeals = analytics && analytics.top_deals ? analytics.top_deals : [];

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-[#0a0a0a] border-b border-[#D4AF37]/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <Sparkles className="w-6 h-6 text-[#D4AF37]" />
              <h1 className="text-2xl font-serif font-bold text-[#D4AF37]">Admin Panel</h1>
            </div>
            <div className="flex space-x-4">
              <button
                onClick={() => navigate('/admin/retailers')}
                className="px-4 py-2 bg-[#1a1a1a] hover:bg-[#D4AF37]/20 border border-[#D4AF37]/30 rounded-lg transition-all flex items-center space-x-2"
                data-testid="nav-retailers-btn"
              >
                <Store className="w-5 h-5" />
                <span>Retailers</span>
              </button>
              <button
                onClick={() => navigate('/admin/deals')}
                className="px-4 py-2 bg-[#1a1a1a] hover:bg-[#D4AF37]/20 border border-[#D4AF37]/30 rounded-lg transition-all flex items-center space-x-2"
                data-testid="nav-deals-btn"
              >
                <Package className="w-5 h-5" />
                <span>Deals</span>
              </button>
              <button
                onClick={handleLogout}
                className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 rounded-lg transition-all flex items-center space-x-2"
                data-testid="logout-btn"
              >
                <LogOut className="w-5 h-5" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h2 className="text-3xl font-bold text-[#D4AF37] mb-8" data-testid="dashboard-title">Analytics Dashboard</h2>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <div className="bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] rounded-xl border border-[#D4AF37]/20 p-6" data-testid="total-clicks-card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-300">Total Clicks</h3>
              <MousePointerClick className="w-8 h-8 text-[#D4AF37]" />
            </div>
            <p className="text-4xl font-bold text-[#D4AF37]" data-testid="total-clicks-count">{totalClicks}</p>
            <p className="text-sm text-gray-500 mt-2">All time clicks tracked</p>
          </div>

          <div className="bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] rounded-xl border border-[#D4AF37]/20 p-6" data-testid="recent-clicks-card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-300">Last 7 Days</h3>
              <TrendingUp className="w-8 h-8 text-[#D4AF37]" />
            </div>
            <p className="text-4xl font-bold text-[#D4AF37]" data-testid="recent-clicks-count">{recentClicks}</p>
            <p className="text-sm text-gray-500 mt-2">Clicks in the past week</p>
          </div>
        </div>

        {/* Top Performers */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Top Retailers */}
          <div className="bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] rounded-xl border border-[#D4AF37]/20 p-6" data-testid="top-retailers-card">
            <h3 className="text-xl font-bold text-[#D4AF37] mb-4 flex items-center">
              <BarChart className="w-6 h-6 mr-2" />
              Top Retailers by Clicks
            </h3>
            {topRetailers.length > 0 ? (
              <div className="space-y-3">
                {topRetailers.map((retailer, index) => (
                  <div key={index} className="flex justify-between items-center p-3 bg-[#0a0a0a] rounded-lg" data-testid={`top-retailer-${index}`}>
                    <span className="text-gray-300">{retailer._id}</span>
                    <span className="text-[#D4AF37] font-bold">{retailer.clicks} clicks</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">No click data yet</p>
            )}
          </div>

          {/* Top Deals */}
          <div className="bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] rounded-xl border border-[#D4AF37]/20 p-6" data-testid="top-deals-card">
            <h3 className="text-xl font-bold text-[#D4AF37] mb-4 flex items-center">
              <BarChart className="w-6 h-6 mr-2" />
              Top Deals by Clicks
            </h3>
            {topDeals.length > 0 ? (
              <div className="space-y-3">
                {topDeals.map((deal, index) => (
                  <div key={index} className="flex justify-between items-center p-3 bg-[#0a0a0a] rounded-lg" data-testid={`top-deal-${index}`}>
                    <span className="text-gray-300 text-sm">{deal._id}</span>
                    <span className="text-[#D4AF37] font-bold">{deal.clicks} clicks</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">No click data yet</p>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-2 gap-6">
          <button
            onClick={() => navigate('/admin/retailers')}
            className="bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] rounded-xl border border-[#D4AF37]/20 p-6 hover:border-[#D4AF37] transition-all text-left"
            data-testid="manage-retailers-btn"
          >
            <Store className="w-12 h-12 text-[#D4AF37] mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">Manage Retailers</h3>
            <p className="text-gray-400">Add or update affiliate retailer links</p>
          </button>

          <button
            onClick={() => navigate('/admin/deals')}
            className="bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] rounded-xl border border-[#D4AF37]/20 p-6 hover:border-[#D4AF37] transition-all text-left"
            data-testid="manage-deals-btn"
          >
            <Package className="w-12 h-12 text-[#D4AF37] mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">Manage Featured Deals</h3>
            <p className="text-gray-400">Create and edit weekly featured deals</p>
          </button>
        </div>

        {/* Back to Site */}
        <div className="mt-8 text-center">
          <button
            onClick={() => navigate('/')}
            className="text-gray-400 hover:text-[#D4AF37] transition-colors"
            data-testid="back-to-site-link"
          >
            ← Back to main site
          </button>
        </div>
      </main>
    </div>
  );
}

export default AdminDashboard;
